import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  originCleared,
  originResolved,
  radiusChanged,
  searchCleared,
  searchSubmitted,
  selectFilters,
  type Origin,
} from './filtersSlice';
import { UK_TOWNS, type Town } from './ukTowns';
import { useGetOverviewQuery } from './eventsApi';

/*
 * The search lives in the address bar.
 *
 * <p>It used to live only in memory, so a search was invisible to the browser: the back button left
 * the site instead of returning to the homepage, a refresh threw the results away, and a search
 * couldn't be sent to anyone. Now `/?where=london&within=25` *is* the search, and the store follows
 * the URL rather than the other way round.
 *
 * <p>Only where and how far. The age, price and keyword filters narrow results already loaded and
 * never cause a fetch, so they stay as page state.
 *
 * <p>"Near me" is written as `where=near-me` with no coordinates. Someone's position has no place
 * in a URL that ends up in browser history, server logs and anything they paste it into.
 */

export const WHERE = 'where';
export const WITHIN = 'within';
export const ANYWHERE_PARAM = 'anywhere';
export const NEAR_ME_PARAM = 'near-me';

export function townSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

export function townFromSlug(slug: string): Town | undefined {
  return UK_TOWNS.find((town) => townSlug(town.name) === slug);
}

export function paramsFor(origin: Origin | null, radiusMiles: number): URLSearchParams {
  if (!origin) return new URLSearchParams({ [WHERE]: ANYWHERE_PARAM });
  const where = origin.source === 'geo' ? NEAR_ME_PARAM : townSlug(origin.label);
  return new URLSearchParams({ [WHERE]: where, [WITHIN]: String(radiusMiles) });
}

/** How many searches back the overview is, so "All events" can step back rather than forward. */
interface SearchHistoryState {
  searchesSinceOverview?: number;
}

/**
 * Starting a search, and leaving one.
 *
 * <p>Navigation, not dispatches: pushing the URL is what makes the browser's back button work, and
 * the store catches up through {@link useUrlDrivenSearch}. Scroll is left where it is — the page
 * changes below the search bar, and throwing the reader to the top is what made it feel abrupt.
 */
export function useSearchNavigation() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { origin, radiusMiles } = useAppSelector(selectFilters);

  const search = useCallback(
    (at: Origin | null = origin, radius: number = radiusMiles) => {
      const next = paramsFor(at, radius);
      const previous = (location.state as SearchHistoryState | null)?.searchesSinceOverview ?? 0;
      if (next.toString() === params.toString()) {
        // The same search again: nothing for the address bar to record, and a second history entry
        // would take two presses of Back to undo. It still runs, from the top of the results.
        dispatch(searchSubmitted());
        return;
      }
      setParams(next, {
        preventScrollReset: true,
        state: { searchesSinceOverview: previous + 1 } satisfies SearchHistoryState,
      });
    },
    [origin, radiusMiles, params, location.state, setParams, dispatch],
  );

  const backToOverview = useCallback(() => {
    const steps = (location.state as SearchHistoryState | null)?.searchesSinceOverview ?? 0;
    // Stepping back returns to the overview exactly as it was left, scroll position included.
    // Arriving from a shared link there is nothing to step back to, so go there instead.
    if (steps > 0) navigate(-steps);
    else navigate('/', { preventScrollReset: true });
  }, [location.state, navigate]);

  return { search, backToOverview, hasSearch: params.has(WHERE) };
}

/**
 * Keeps the store in step with the address bar: a URL with a search runs it, a URL without one
 * returns to the overview. Mounted once, by the events page.
 */
export function useUrlDrivenSearch(): { where: string | null } {
  const [params] = useSearchParams();
  const where = params.get(WHERE);
  const within = Number(params.get(WITHIN));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { origin } = useAppSelector(selectFilters);
  // Where the cities outside the town list are. Already loaded for the homepage, and on a shared
  // link it arrives a moment after the URL does — which is why the effect waits for it.
  const { data: overview } = useGetOverviewQuery();

  /*
   * The address this has already acted on.
   *
   * <p>The effect re-runs when the overview arrives, which is not the same thing as the address
   * changing, and re-running the whole block on that arrival undid work the reader had done:
   * on the homepage it cleared a town they had chosen from the Where box but not yet searched —
   * the box emptied under them and the radius disappeared — and on a results page it re-applied
   * the search and dropped their keyword chips. `undefined` means nothing handled yet, which is a
   * different state from an address with no search in it.
   */
  const handled = useRef<string | undefined>(undefined);
  const address = where === null ? '' : `${where}|${within}`;

  useEffect(() => {
    // `undefined` on the first run, so arriving on the homepage still clears whatever a previous
    // page left in the store.
    const addressChanged = handled.current === undefined || handled.current !== address;

    if (!where) {
      if (addressChanged) dispatch(searchCleared());
      handled.current = address;
      return;
    }
    // The same search as last time: it is already running. Only a search still waiting for the
    // overview reaches here twice, because that one deliberately doesn't record itself below.
    if (!addressChanged) return;

    if (Number.isFinite(within) && within > 0) dispatch(radiusChanged(within));

    if (where === ANYWHERE_PARAM) {
      dispatch(originCleared());
      dispatch(searchSubmitted());
      handled.current = address;
      return;
    }

    if (where === NEAR_ME_PARAM) {
      // The coordinates were never in the URL, so after a refresh or from a shared link there is
      // nothing to search around. The overview beats a silent search of somewhere else.
      if (origin?.source === 'geo') {
        dispatch(searchSubmitted());
        handled.current = address;
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    const town = townFromSlug(where);
    if (town) {
      dispatch(originResolved({ lat: town.lat, lng: town.lng, label: town.name, source: 'town' }));
      dispatch(searchSubmitted());
      handled.current = address;
      return;
    }

    /*
     * Not one of the 26 towns — New Malden, Walsall, anywhere else an organiser has used. The
     * homepage already knows where those are, because the server works out the middle of each
     * city's venues for the map, so the slug is matched against that rather than refusing.
     *
     * Coordinates stay out of the address. A city centre is not somebody's location, but a URL
     * carrying numbers is one nobody can read, and "?where=new-malden" is a link worth sharing.
     */
    const city = [...(overview?.cities ?? []), ...(overview?.otherCities ?? [])].find(
      (candidate) => townSlug(candidate.name) === where,
    );
    if (city?.latitude != null && city.longitude != null) {
      dispatch(originResolved({ lat: city.latitude, lng: city.longitude, label: city.name, source: 'town' }));
      dispatch(searchSubmitted());
      handled.current = address;
      return;
    }

    // Still loading the overview? Wait for it rather than throwing the reader back to the
    // homepage — on a cold load of a shared link the data arrives a moment after the URL. The
    // address stays unrecorded, which is what lets its arrival try this again.
    if (overview) {
      navigate('/', { replace: true });
    }
    // Deliberately only the address: `origin` is read, not followed. Following it would re-run the
    // search whenever someone shared their location for distances, which is not a new search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, where, within, dispatch, navigate, overview]);

  return { where };
}

/**
 * "Events near Birmingham", "Events near you", "Events across the UK".
 *
 * @param placeName what the search resolved to, which is the only thing that knows the name of a
 *                  city outside the town list. Without it, a New Malden search was headed "Events
 *                  across the UK" while showing events near New Malden.
 */
export function resultsHeading(where: string | null, placeName?: string): string {
  if (!where || where === ANYWHERE_PARAM) return 'Events across the UK';
  if (where === NEAR_ME_PARAM) return 'Events near you';
  const name = townFromSlug(where)?.name ?? placeName;
  return name ? `Events near ${name}` : 'Events across the UK';
}
